import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { track } from "../lib/analytics";

export function VideoCard({ video, index = 0, autoPlayOnClick = true }) {
  const [playing, setPlaying] = useState(false);
  const thumb = video.thumbnail || `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`;
  const start = () => {
    track("video_play", { releaseId: video.releaseId, metadata: { youtubeId: video.youtubeId, slug: video.slug, placement: "card" } });
    setPlaying(true);
  };
  return (
    <motion.div
      data-testid={`video-${video.youtubeId}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 2) * 0.1 }}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-surface/60"
    >
      <div className="relative aspect-video overflow-hidden bg-black">
        {playing ? (
          <iframe
            title={video.title}
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : autoPlayOnClick ? (
          <button onClick={start} aria-label={`Play ${video.title}`} className="relative block h-full w-full">
            <img src={thumb} alt={`${video.title} — DJ Código video thumbnail`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`; }} />
            <span className="absolute inset-0 bg-ink/30" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-black transition-transform group-hover:scale-110">
              <Play size={26} fill="currentColor" className="ml-1" />
            </span>
          </button>
        ) : (
          <Link to={`/videos/${video.slug}`} className="relative block h-full w-full">
            <img src={thumb} alt={`${video.title} — DJ Código video thumbnail`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute inset-0 bg-ink/30" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-black"><Play size={26} fill="currentColor" className="ml-1" /></span>
          </Link>
        )}
      </div>
      <div className="p-4">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gold">{video.videoType?.replace("_", " ")}</span>
        <h3 className="mt-1 font-display text-base font-semibold text-white">
          {video.slug ? <Link to={`/videos/${video.slug}`} data-testid={`video-link-${video.slug}`} className="transition-colors hover:text-gold">{video.title}</Link> : video.title}
        </h3>
      </div>
    </motion.div>
  );
}

export default function VideosSection({ videos, limit = 4 }) {
  if (!videos || videos.length === 0) return null;
  return (
    <section id="videos" data-testid="videos-section" className="px-6 py-16 sm:px-10 sm:py-24 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Latest from YouTube</p>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">New Videos</h2>
            </div>
            <Link to="/videos" data-testid="videos-see-all" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white/60 transition-colors hover:text-gold">Watch all <ArrowRight size={14} /></Link>
          </div>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2">
          {videos.slice(0, limit).map((v, i) => <VideoCard key={v.youtubeId} video={v} index={i} />)}
        </div>
      </div>
    </section>
  );
}
